import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Spinner,
  Badge,
  Card,
  InputGroup,
  Tooltip,
  OverlayTrigger,
  DropdownButton,
  Dropdown
} from 'react-bootstrap';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { ArrowLeft, Download, FileEarmarkSpreadsheet, InfoCircle, Printer, Search } from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import OutletSelect from '../../components/OutletSelect';
import { MAX_DATE_RANGE_DAYS, DATE_RANGE_ERROR_MESSAGE } from '../../constants/dateConstants';

const ProductSalesReport = () => {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const navigate = useNavigate();

  const accessToken = localStorage.getItem("authToken");
  const [productList, setProductList] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCategoryData, setSelectedCategoryData] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [selectedOutletName, setSelectedOutletName] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [clicked, setClicked] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
  const [classType, setClassType] = useState(null);

  // ── Sorting state ──────────────────────────────────────────────────────────
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const animatedComponents = makeAnimated();

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      borderRadius: "6px",
      borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
      "&:hover": {
        borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
  };

  useEffect(() => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
    const end = new Date(currentDate.setHours(23, 59, 59, 999));

    setStartDate(start);
    setEndDate(end);
    getCategoryList();
  }, []);

  useEffect(() => {
    if (searchText === "" && isTouched === true) {
      console.log("hELLO");
      getProductData(startDate, endDate, selectedOutlet.value, selectedCategory, classType);
    }
  }, [searchText]);

  const categoryOptions = categoryList.map((category) => ({
    value: category._id,
    label: category.name,
  }));

  const outletOptions = outlets.map((outlet) => ({
    value: outlet._id,
    label: `${outlet.name} (${outlet.city})`,
  }));

  const classTypeOptions = [
    { value: 'COMBOS', label: 'COMBOS' },
    { value: 'DESSERTS', label: 'DESSERTS' },
    { value: 'EXTRAS', label: 'EXTRAS' },
    { value: 'FOOD', label: 'FOOD' },
    { value: 'BEVERAGE', label: 'BEVERAGE' },
  ];

  // ── Sorting helpers ────────────────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] ?? 0;
    const bVal = b[sortConfig.key] ?? 0;
    if (typeof aVal === 'string') {
      return sortConfig.direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) return <span className="ms-1 text-muted" style={{ fontSize: '11px' }}>⇅</span>;
    return <span className="ms-1" style={{ fontSize: '11px' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };
  // ──────────────────────────────────────────────────────────────────────────

  const handleDownloadExcel = async (costing) => {
    setDownloadBtnLoading(true);
    const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
    const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
    getProductData(adjustedStartDate, adjustedEndDate, selectedOutlet.value, selectedCategory, classType, true, costing);
  };

  const getProductData = async (startDate, endDate, outlet, category, cType, download, costing) => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };

      const updatedStartDate = startDate ? startDate.toISOString().split("T")[0] : null;
      const updatedEndDate = endDate ? endDate.toISOString().split("T")[0] : null;

      const queryParams = new URLSearchParams();
      if (updatedStartDate) queryParams.append('startDate', updatedStartDate);
      if (updatedEndDate) queryParams.append('endDate', updatedEndDate);
      if (outlet) queryParams.append('outlet', outlet);
      if (category) queryParams.append('category', category);
      if (cType) queryParams.append('classType', cType);
      if (download) queryParams.append('isDownload', download);
      if (costing) queryParams.append('isCosting', costing);

      const response = await axios.get(`order/getProductsReportForAdmin?${queryParams.toString()}`, {
        headers,
        responseType: download ? 'blob' : 'json'
      });

      if (download) {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);

        const fileName = `${selectedOutletName} Product Sales Report (${updatedStartDate} to ${updatedEndDate}).xlsx`;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        toast.success('Excel file downloaded successfully!', {
          position: "top-right",
          autoClose: 3000,
          theme: "light"
        });
      } else {
        console.log("Response Data:", response?.data?.data);
        setProductList(response?.data?.data || []);
        setFilteredData(response?.data?.data || []);
      }

    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
      setDownloadBtnLoading(false);
    }
  };

  const getCategoryList = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("masters/category/get", { headers });

      if (response?.data?.status === true) {
        setCategoryList(response?.data?.data?.categories || []);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    let errorMessage = 'An error occurred';
    if (error.response?.status === 406) {
      errorMessage = error.response?.data?.error?.message || error.response?.data?.error;
    } else if (error.response?.status === 412) {
      errorMessage = error.response?.data?.data?.message;
    } else if (error.response?.status === 500) {
      errorMessage = error.response?.data?.error;
    }

    toast.error(errorMessage, {
      position: "top-right",
      autoClose: 3000,
      theme: "light",
    });
  };

  const handleEndDateChange = (date) => {
    const start = new Date(startDate);
    const end = new Date(date);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const maxEndDate = new Date(start);
    maxEndDate.setDate(start.getDate() + MAX_DATE_RANGE_DAYS);

    if (maxEndDate && startDate && maxEndDate < startDate) {
      setError('End date cannot be earlier than start date');
    } else {
      setError('');
      setEndDate(date);
    }
  };

  const handleSubmit = async (e) => {
    console.log("Heddllo");
    e.preventDefault();
    console.log("Hello 1");

    setClicked(true);
    setBtnLoading(true);

    const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
    const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));

    await getProductData(adjustedStartDate, adjustedEndDate, selectedOutlet.value, selectedCategory, classType);

    toast.success("Data Fetched Successfully", {
      position: "top-right",
      autoClose: 3000,
      theme: "light",
    });
    setBtnLoading(false);
  };

  const handlePrintReport = () => {
    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    const totalSales = filteredData.reduce((total, product) => total + (product?.totalQuantity || 0), 0);
    const totalAddonPrice = filteredData.reduce((total, product) => total + (Number(product?.addonOfferPrice) || 0), 0).toFixed(2);
    const totalActualTaxable = filteredData.reduce((total, product) => total + (Number(product?.actualTaxable) || 0), 0).toFixed(2);
    const totalDisc = filteredData.reduce((total, product) => total + (Number(product?.actualTaxable || 0) - Number(product?.totalTaxable || 0)), 0).toFixed(2);
    const totalAfterDiscountTaxable = filteredData.reduce((total, product) => total + (Number(product?.totalTaxable) || 0), 0).toFixed(2);

    const tableContent = `
      <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
        <thead>
          <tr style="background-color: #f8f9fa;">
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 50px;">#</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Category & Product</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px;">Type</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px; text-align: right;">Rate</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px; text-align: right;">Avg. Price</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px; text-align: center;">Sales</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px; text-align: right;">Addon Price</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 120px; text-align: right;">Actual Taxable</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 120px; text-align: right;">Discount</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 120px; text-align: right;">After Dis. Taxable</th>
          </tr>
        </thead>
        <tbody>
          ${filteredData?.length > 0 ?
        filteredData.map((product, index) => `
              <tr>
                <td style="border: 1px solid #dee2e6; padding: 8px; color: #6c757d;">${index + 1}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">
                  <div style="color: #212529;">${product?.productName || '----'}</div>
                  <div style="font-weight: 600; color: #0d6efd;">${product?.categoryName || '----'}</div>
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">
                  <span style="display: inline-block; padding: 0.25em 0.4em; font-size: 75%; font-weight: 700; line-height: 1; text-align: center; white-space: nowrap; vertical-align: baseline; border-radius: 0.25rem; text-transform: uppercase; background-color: ${product?.foodType === "NON-VEG" ? '#dc3545' : '#198754'}; color: white;">
                    ${product?.foodType}
                  </span>
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ${product?.rate ? '₹ ' + product.rate.toFixed(2) : '----'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ${product?.averageRateOfProduct ? '₹ ' + product.averageRateOfProduct.toFixed(2) : '----'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${product?.totalQuantity || 0}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ₹ ${product?.addonOfferPrice?.toFixed(2) || '0.00'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ₹ ${product?.actualTaxable?.toFixed(2) || '0.00'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ₹ ${(product?.actualTaxable - product.totalTaxable).toFixed(2) || '0.00'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ₹ ${product?.totalTaxable?.toFixed(2) || '0.00'}
                </td>
              </tr>
            `).join("") : `
              <tr>
                <td colspan="10" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
                  <i style="font-size: 16px; color: #6c757d;"></i>
                  <p style="margin: 8px 0 0; color: #6c757d;">No products found for the selected criteria</p>
                </td>
              </tr>`
      }
          ${filteredData?.length > 0 ? `
            <tr style="font-weight: bold; background-color: #f8f9fa;">
              <td colspan="5" style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">TOTAL</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${totalSales}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalAddonPrice}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalActualTaxable}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalDisc}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalAfterDiscountTaxable}</td>
            </tr>` : ''}
        </tbody>
      </table>
    `;

    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Product Sales Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .title { font-size: 18px; font-weight: bold; }
            .date-range { font-size: 14px; color: #666; }
            table { width: 100%; border-collapse: collapse; }
            th { background-color: #f8f9fa; text-align: left; }
          </style>
        </head>
        <body onload="window.print();window.close()">
          <div class="header">
            <div class="title">Product Sales Report</div>
            <div class="date-range">${formattedStartDate} to ${formattedEndDate}</div>
          </div>
          ${tableContent}
        </body>
      </html>
    `);
    win.document.close();
  };

  const handleSearchProducts = () => {
    const trimmedSearchText = searchText.trim().toLowerCase();
    if (trimmedSearchText) {
      const filtered = productList?.filter(product =>
        product?.productName?.toLowerCase().includes(trimmedSearchText)
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(productList);
    }
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0 fw-bold d-inline-block">
                <i className="bi bi-box-seam me-2"></i>PRODUCT SALES REPORT
              </h4>
            </div>
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate('/reports/all-reports')}
                className="me-3 d-flex align-items-center fw-semibold"
                style={{ height: '38px' }}
              >
                <ArrowLeft className="me-1" /> Back to Reports
              </Button>
            </div>
          </div>
          <hr className='mt-0 mb-0' />

          <div className="card-body">
            {/* Filters Section */}
            <Form onSubmit={handleSubmit}>

              <div className="p-3 bg-light rounded-3">
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group controlId="outletSelect">
                      <OutletSelect
                        value={selectedOutlet}
                        onChange={(option) => setSelectedOutlet(option)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="categorySelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-tags me-2"></i>Category
                      </Form.Label>
                      <Select
                        options={categoryOptions}
                        value={categoryOptions.find(option => option.value === selectedCategory)}
                        onChange={(selectedOption) => {
                          const selectCategory = selectedOption?.value;
                          setSelectedCategory(selectCategory);
                        }}
                        isClearable
                        styles={customStyles}
                        placeholder="Select Category"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group controlId="classTypeSelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-list-check me-2"></i>Class Type
                      </Form.Label>
                      <Select
                        options={classTypeOptions}
                        value={classTypeOptions.find(
                          (option) => option.value === classType
                        )}
                        onChange={(selectedOption) => {
                          setClassType(selectedOption?.value || null);
                        }}
                        isClearable
                        styles={customStyles}
                        placeholder="Select Class Type"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group controlId="startDate">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>Start Date
                      </Form.Label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          if (endDate && date && endDate > new Date(date.getTime() + MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000)) {
                            setEndDate(null);
                          }
                        }}
                        dateFormat="dd-MM-yyyy"
                        className="form-control border-secondary"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group controlId="endDate">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>End Date
                      </Form.Label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => {
                          if (startDate && date > new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000)) {
                            setError(DATE_RANGE_ERROR_MESSAGE);
                          } else {
                            setError("");
                            handleEndDateChange(date);
                          }
                        }}
                        dateFormat="dd-MM-yyyy"
                        minDate={startDate}
                        maxDate={startDate ? new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000) : null}
                        className="form-control border-secondary"
                      />
                      {error && <div className="text-danger small mt-1">{error}</div>}
                    </Form.Group>
                  </Col>

                </Row>
              </div>

              {/* Search and Actions Section */}
              <div className="mb-4 p-3 bg-light rounded-3">
                <Row className="g-3 align-items-center">
                  <Col md={3} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={!startDate || !endDate}
                    >
                      {btnLoading ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        "Generate Report"
                      )}
                    </Button>
                  </Col>
                  <Col md={3}>
                    <DropdownButton
                      variant="outline-success"
                      className='fw-semibold w-100'
                      title={
                        <>
                          <FileEarmarkSpreadsheet className="me-2" />
                          {downloadBtnLoading ? <Spinner size='sm' /> : 'Download Report'}
                        </>
                      }
                      disabled={!filteredData.length}
                    >
                      <Dropdown.Item
                        onClick={() => handleDownloadExcel(false)}
                        disabled={downloadBtnLoading}
                      >
                        <FileEarmarkSpreadsheet className="me-2 text-success" />
                        Download without costing
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDownloadExcel(true)}
                        disabled={downloadBtnLoading}
                      >
                        <FileEarmarkSpreadsheet className="me-2 text-success" />
                        Download with costing
                      </Dropdown.Item>
                    </DropdownButton>
                  </Col>
                  <Col md={4}>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Search by product name"
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setIsTouched(true);
                        }}
                        className="border-secondary"
                      />
                      <Button
                        variant="primary"
                        style={{ zIndex: 0 }}
                        onClick={handleSearchProducts}
                        disabled={!searchText.trim()}
                      >
                        <Search /> Search
                      </Button>
                    </InputGroup>
                  </Col>

                </Row>
              </div>
            </Form>


            {/* Results Section */}
            {!clicked ? (
              <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                <div className="text-center">
                  <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                  <h5 className="text-muted mt-3">
                    Please select filters and generate report
                  </h5>
                </div>
              </div>
            ) : (
              <div className="table-responsive border rounded-3">
                <Table striped bordered hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th width="50">#</th>

                      {/* Category & Product */}
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('productName')}
                      >
                        Category & Product <SortIcon colKey="productName" />
                      </th>

                      {/* Type */}
                      <th
                        width="100"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('foodType')}
                      >
                        Type <SortIcon colKey="foodType" />
                      </th>

                      {/* Rate */}
                      <th
                        width="100"
                        className="text-end"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('rate')}
                      >
                        Rate <SortIcon colKey="rate" />
                      </th>

                      {/* Avg. Price */}
                      <th
                        width="100"
                        className="text-end"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('averageRateOfProduct')}
                      >
                        Avg. Price <SortIcon colKey="averageRateOfProduct" />
                      </th>

                      {/* Sales */}
                      <th
                        width="100"
                        className="text-center"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('totalQuantity')}
                      >
                        Sales <SortIcon colKey="totalQuantity" />
                      </th>

                      {/* Addon Price */}
                      <th
                        width="100"
                        className="text-end"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('addonOfferPrice')}
                      >
                        Addon Price <SortIcon colKey="addonOfferPrice" />
                      </th>

                      {/* Actual Taxable */}
                      <th
                        width="120"
                        className="text-end"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('actualTaxable')}
                      >
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          Actual Taxable <SortIcon colKey="actualTaxable" />
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="actual-taxable-tooltip">
                                (Average Price * Sales Count)
                              </Tooltip>
                            }
                          >
                            <span
                              style={{ cursor: 'pointer' }}
                              className="ms-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InfoCircle className="text-muted" size={14} />
                            </span>
                          </OverlayTrigger>
                        </div>
                      </th>

                      {/* Discount */}
                      <th
                        width="120"
                        className="text-end"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('actualTaxable')}
                      >
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          Discount <SortIcon colKey="actualTaxable" />
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="discount-tooltip">
                                (Before Discount Taxable - After Discount Taxable)
                              </Tooltip>
                            }
                          >
                            <span
                              style={{ cursor: 'pointer' }}
                              className="ms-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InfoCircle className="text-muted" size={14} />
                            </span>
                          </OverlayTrigger>
                        </div>
                      </th>

                      {/* After Dis. Taxable */}
                      <th
                        width="120"
                        className="text-end"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('totalTaxable')}
                      >
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          After Dis. Taxable <SortIcon colKey="totalTaxable" />
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="after-discount-tooltip">
                                Taxable amount after applying all discounts
                              </Tooltip>
                            }
                          >
                            <span
                              style={{ cursor: 'pointer' }}
                              className="ms-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InfoCircle className="text-muted" size={14} />
                            </span>
                          </OverlayTrigger>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="12" className="text-center py-4">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2 mb-0">Loading product data...</p>
                        </td>
                      </tr>
                    ) : sortedData?.length > 0 ? (
                      <>
                        {sortedData.map((product, index) => (
                          <tr key={index}>
                            <td className="text-muted">{index + 1}</td>
                            <td>
                              <div className="text-dark">{product?.productName}</div>
                              <div className="fw-semibold text-primary">{product?.categoryName}</div>
                            </td>
                            <td>
                              <Badge
                                bg={product?.foodType === "NON-VEG" ? "danger" : "success"}
                                className="text-uppercase"
                              >
                                {product?.foodType}
                              </Badge>
                            </td>
                            <td className="text-end">
                              {product?.rate ?
                                `₹ ${product.rate.toFixed(2)}` : '----'}
                            </td>
                            <td className="text-end">
                              {product?.averageRateOfProduct ?
                                `₹ ${product?.averageRateOfProduct?.toFixed(2)}` : '----'}
                            </td>
                            <td className="text-center">{product?.totalQuantity}</td>
                            <td className="text-end">₹ {product?.addonOfferPrice?.toFixed(2)}</td>
                            <td className="text-end">₹ {product?.actualTaxable?.toFixed(2)}</td>
                            <td className="text-end">
                              ₹ {(product?.actualTaxable - product.totalTaxable).toFixed(2)}
                            </td>
                            <td className="text-end">₹ {product?.totalTaxable?.toFixed(2)}</td>
                          </tr>
                        ))}
                        {/* Totals row always uses filteredData (unaffected by sort order) */}
                        <tr className="bg-light">
                          <td colSpan="5" className="text-end fw-bold">TOTAL</td>
                          <td className="text-center fw-bold">
                            {filteredData.reduce((total, product) => total + (product?.totalQuantity || 0), 0)}
                          </td>
                          <td className="text-end fw-bold">
                            ₹ {filteredData.reduce((total, product) => total + (product?.addonOfferPrice || 0), 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold">
                            ₹ {filteredData.reduce((total, product) => total + (product?.actualTaxable || 0), 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold">
                            ₹ {filteredData.reduce((total, product) => total + ((product.actualTaxable - product.totalTaxable) || 0), 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold">
                            ₹ {filteredData.reduce((total, product) => total + (product?.totalTaxable || 0), 0).toFixed(2)}
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                          <p className="mt-2 mb-0 text-muted">
                            No products found for the selected criteria
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductSalesReport;




// import React, { useEffect, useState } from 'react';
// import {
//   Button, Col, Form, Row, Table, Spinner,
//   InputGroup, Tooltip, OverlayTrigger,
//   DropdownButton, Dropdown
// } from 'react-bootstrap';
// import axios from '../../api/axios';
// import { toast } from 'react-toastify';
// import DatePicker from 'react-datepicker';
// import { ArrowLeft, FileEarmarkSpreadsheet, InfoCircle, Search } from 'react-bootstrap-icons';
// import Header from '../../layouts/Header';
// import { useNavigate } from 'react-router-dom';
// import Select from 'react-select';
// import OutletSelect from '../../components/OutletSelect';
// import { MAX_DATE_RANGE_DAYS, DATE_RANGE_ERROR_MESSAGE } from '../../constants/dateConstants';

// /* ─── helpers ─── */
// const safeArray = (val) => (Array.isArray(val) ? val : []);
// const fmt = (n) => `₹ ${Number(n || 0).toFixed(2)}`;
// const foodTypeMeta = {
//   'NON-VEG': { bg: '#dc3545', label: 'Non-Veg' },
//   VEGAN:     { bg: '#f59e0b', label: 'Vegan'   },
//   VEG:       { bg: '#16a34a', label: 'Veg'     },
// };

// const VIEW_COMBINED = 'combined';
// const VIEW_GROUPED  = 'grouped';
// const VIEW_CATEGORY = 'category';

// const ProductSalesReport = () => {
//   const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
//   const [skin, setSkin] = useState(currentSkin);
//   const navigate = useNavigate();
//   const accessToken = localStorage.getItem('authToken');

//   const [reportData,          setReportData]          = useState([]);
//   const [categoryList,        setCategoryList]        = useState([]);
//   const [selectedOutlets,     setSelectedOutlets]     = useState([]);
//   const [selectedCategories,  setSelectedCategories]  = useState([]);
//   const [startDate,           setStartDate]           = useState(null);
//   const [endDate,             setEndDate]             = useState(null);
//   const [classType,           setClassType]           = useState(null);
//   const [loading,             setLoading]             = useState(false);
//   const [btnLoading,          setBtnLoading]          = useState(false);
//   const [downloadBtnLoading,  setDownloadBtnLoading]  = useState(false);
//   const [clicked,             setClicked]             = useState(false);
//   const [error,               setError]               = useState('');
//   const [searchText,          setSearchText]          = useState('');
//   const [isTouched,           setIsTouched]           = useState(false);
//   const [viewMode,            setViewMode]            = useState(VIEW_CATEGORY);
//   const [sortConfig,          setSortConfig]          = useState({ key: null, direction: 'asc' });
//   const [activeOutletTab,     setActiveOutletTab]     = useState(0);
//   const [categoryCollapsed,   setCategoryCollapsed]   = useState({});

//   useEffect(() => {
//     const now   = new Date();
//     const first = new Date(now.getFullYear(), now.getMonth(), 1);
//     const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
//     setStartDate(new Date(first.setHours(23, 59, 59, 999)));
//     setEndDate(new Date(today.setHours(23, 59, 59, 999)));
//     getCategoryList();
//   }, []);

//   /* ─── derived data ─── */
//   const filteredReportData = safeArray(reportData).map((outletData) => {
//     const search = searchText.trim().toLowerCase();
//     if (!search) return outletData;
//     return {
//       ...outletData,
//       products:       safeArray(outletData.products).filter(p => p.productName?.toLowerCase().includes(search)),
//       unSoldProducts: safeArray(outletData.unSoldProducts).filter(p => p.productName?.toLowerCase().includes(search)),
//     };
//   }).filter(o => safeArray(o.products).length > 0 || safeArray(o.unSoldProducts).length > 0);

//   const hasAnyData = filteredReportData.some(
//     o => safeArray(o.products).length > 0 || safeArray(o.unSoldProducts).length > 0
//   );

//   /* ─── Combined view: merge duplicate products across outlets ─── */
//   const mergeProducts = (outletDataList, key) =>
//     Object.values(
//       outletDataList
//         .flatMap(o =>
//           safeArray(o[key]).map(p => ({ ...p, _outletName: o.outlet?.name }))
//         )
//         .reduce((acc, p) => {
//           const id = p._id || p.productName;
//           if (!acc[id]) {
//             acc[id] = { ...p, _outletNames: [p._outletName] };
//           } else {
//             acc[id].totalQuantity   = (acc[id].totalQuantity   || 0) + (p.totalQuantity   || 0);
//             acc[id].addonOfferPrice = (acc[id].addonOfferPrice || 0) + (p.addonOfferPrice || 0);
//             acc[id].actualTaxable   = (acc[id].actualTaxable   || 0) + (p.actualTaxable   || 0);
//             acc[id].totalTaxable    = (acc[id].totalTaxable    || 0) + (p.totalTaxable    || 0);
//             if (!acc[id]._outletNames.includes(p._outletName)) {
//               acc[id]._outletNames.push(p._outletName);
//             }
//             acc[id].averageRateOfProduct =
//               acc[id].totalQuantity > 0
//                 ? acc[id].totalTaxable / acc[id].totalQuantity
//                 : 0;
//           }
//           return acc;
//         }, {})
//     );

//   const combinedProducts = mergeProducts(filteredReportData, 'products');
//   const combinedUnsold   = mergeProducts(filteredReportData, 'unSoldProducts');

//   /* ─── Category Wise: group all sold/unsold products by category ─── */
//   const groupedByCategory = (() => {
//     const map = {};
//     filteredReportData.flatMap(o =>
//       safeArray(o.products).map(p => ({ ...p, _outletName: o.outlet?.name }))
//     ).forEach(p => {
//       const key = p.categoryName || 'Uncategorized';
//       if (!map[key]) map[key] = [];
//       map[key].push(p);
//     });
//     return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
//   })();

//   const groupedUnsoldByCategory = (() => {
//     const map = {};
//     filteredReportData.flatMap(o =>
//       safeArray(o.unSoldProducts).map(p => ({ ...p, _outletName: o.outlet?.name }))
//     ).forEach(p => {
//       const key = p.categoryName || 'Uncategorized';
//       if (!map[key]) map[key] = [];
//       map[key].push(p);
//     });
//     return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
//   })();

//   /* ─── sort ─── */
//   const handleSort = (key) =>
//     setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));

//   const sortProducts = (products) => {
//     if (!sortConfig.key) return safeArray(products);
//     return [...safeArray(products)].sort((a, b) => {
//       const aVal = a[sortConfig.key] ?? 0;
//       const bVal = b[sortConfig.key] ?? 0;
//       if (typeof aVal === 'string')
//         return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
//       return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
//     });
//   };

//   const SortIcon = ({ colKey }) => (
//     sortConfig.key !== colKey
//       ? <span style={{ opacity: 0.3, fontSize: 10, marginLeft: 3 }}>⇅</span>
//       : <span style={{ fontSize: 10, marginLeft: 3, color: '#0d6efd' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
//   );

//   /* ─── grand totals ─── */
//   const grandTotals = (() => {
//     const allSold = filteredReportData?.flatMap(o => safeArray(o?.products));
//     return {
//       qty:       allSold?.reduce((s, p) => s + (p?.totalQuantity || 0), 0),
//       addon:     allSold?.reduce((s, p) => s + (p?.addonOfferPrice || 0), 0),
//       actual:    allSold?.reduce((s, p) => s + (p?.actualTaxable || 0), 0),
//       disc:      allSold?.reduce((s, p) => s + ((p?.actualTaxable || 0) - (p?.totalTaxable || 0)), 0),
//       afterDisc: allSold?.reduce((s, p) => s + (p?.totalTaxable || 0), 0),
//     };
//   })();

//   /* ─── api ─── */
//   const getCategoryList = async () => {
//     try {
//       const res = await axios.get('masters/category/get', { headers: { Authorization: `Bearer ${accessToken}` } });
//       if (res?.data?.status === true) setCategoryList(res?.data?.data?.categories || []);
//     } catch (e) { handleApiError(e); }
//   };

//   const getProductData = async (sd, ed, outletIds, categoryIds, cType, download, costing) => {
//     try {
//       setLoading(true);
//       const headers = { Authorization: `Bearer ${accessToken}` };
//       const qs = new URLSearchParams();
//       if (sd)       qs.append('startDate',  sd.toISOString().split('T')[0]);
//       if (ed)       qs.append('endDate',    ed.toISOString().split('T')[0]);
//       if (cType)    qs.append('classType',  cType);
//       if (download) qs.append('isDownload', download);
//       if (costing)  qs.append('isCosting',  costing);

//       const res = await axios.post(
//         `order/getProductsReportForAdmin/new?${qs.toString()}`,
//         { outletIds: safeArray(outletIds), categoryIds: safeArray(categoryIds) },
//         { headers, responseType: download ? 'blob' : 'json' }
//       );

//       if (download) {
//         const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
//         const link = document.createElement('a');
//         link.href = window.URL.createObjectURL(blob);
//         const names = selectedOutlets.length > 0 ? selectedOutlets.map(o => o.label).join(', ') : 'All Outlets';
//         link.download = `${names} Product Sales Report (${sd?.toISOString().split('T')[0]} to ${ed?.toISOString().split('T')[0]}).xlsx`;
//         document.body.appendChild(link); link.click();
//         document.body.removeChild(link); window.URL.revokeObjectURL(link.href);
//         toast.success('Excel file downloaded successfully!', { position: 'top-right', autoClose: 3000, theme: 'light' });
//       } else {
//         const raw = res?.data?.data;
//         setReportData(Array.isArray(raw) ? raw : []);
//         setActiveOutletTab(0);
//         setCategoryCollapsed({});
//       }
//     } catch (e) { handleApiError(e); }
//     finally { setLoading(false); setDownloadBtnLoading(false); }
//   };

//   const handleApiError = (e) => {
//     let msg = 'An error occurred';
//     if (e.response?.status === 406) msg = e.response?.data?.error?.message || e.response?.data?.error;
//     else if (e.response?.status === 412) msg = e.response?.data?.data?.message;
//     else if (e.response?.status === 500) msg = e.response?.data?.error;
//     toast.error(msg, { position: 'top-right', autoClose: 3000, theme: 'light' });
//   };

//   const handleEndDateChange = (date) => {
//     if (startDate && new Date(date) < new Date(startDate)) setError('End date cannot be earlier than start date');
//     else { setError(''); setEndDate(date); }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setClicked(true); setBtnLoading(true);
//     const sd = new Date(new Date(startDate).setHours(23, 59, 59, 999));
//     const ed = new Date(new Date(endDate).setHours(23, 59, 59, 999));
//     await getProductData(sd, ed, selectedOutlets.map(o => o.value), selectedCategories.map(c => c.value), classType);
//     toast.success('Data Fetched Successfully', { position: 'top-right', autoClose: 3000, theme: 'light' });
//     setBtnLoading(false);
//   };

//   const handleDownloadExcel = (costing) => {
//     setDownloadBtnLoading(true);
//     const sd = new Date(new Date(startDate).setHours(23, 59, 59, 999));
//     const ed = new Date(new Date(endDate).setHours(23, 59, 59, 999));
//     getProductData(sd, ed, selectedOutlets.map(o => o.value), selectedCategories.map(c => c.value), classType, true, costing);
//   };

//   const toggleCategoryCollapse = (key) => {
//     setCategoryCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
//   };

//   /* ─── options ─── */
//   const categoryOptions  = categoryList.map(c => ({ value: c._id, label: c.name }));
//   const classTypeOptions = ['COMBOS','DESSERTS','EXTRAS','FOOD','BEVERAGE'].map(v => ({ value: v, label: v }));

//   const selectStyles = {
//     control: (p, s) => ({
//       ...p, borderRadius: 8, fontSize: 13,
//       borderColor: s.isFocused ? '#0d6efd' : '#ced4da',
//       boxShadow: s.isFocused ? '0 0 0 3px rgba(13,110,253,.15)' : 'none',
//       '&:hover': { borderColor: '#0d6efd' },
//     }),
//     multiValue:       (p) => ({ ...p, backgroundColor: '#e7f0ff', borderRadius: 6 }),
//     multiValueLabel:  (p) => ({ ...p, color: '#0d6efd', fontWeight: 600 }),
//     multiValueRemove: (p) => ({ ...p, color: '#0d6efd', ':hover': { backgroundColor: '#0d6efd', color: 'white' } }),
//     placeholder:      (p) => ({ ...p, color: '#9ca3af', fontSize: 13 }),
//     option: (p, s) => ({ ...p, fontSize: 13, backgroundColor: s.isSelected ? '#0d6efd' : s.isFocused ? '#e7f0ff' : 'white' }),
//   };

//   /* ─── shared styles ─── */
//   const tdStyle = { verticalAlign: 'middle', padding: '8px 12px', borderColor: '#f1f5f9' };
//   const thStyle = {
//     verticalAlign: 'middle', padding: '10px 12px',
//     fontSize: 11, fontWeight: 700, letterSpacing: '0.6px',
//     textTransform: 'uppercase', color: '#64748b',
//     borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc',
//     cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
//   };

//   const ThSortable = ({ colKey, align = 'left', children }) => (
//     <th style={{ ...thStyle, textAlign: align, width: colKey === 'foodType' ? '10%' : 'auto' }} onClick={() => handleSort(colKey)}>
//       {children}<SortIcon colKey={colKey} />
//     </th>
//   );

//   const InfoTip = ({ id, text }) => (
//     <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{text}</Tooltip>}>
//       <span onClick={e => e.stopPropagation()} style={{ cursor: 'default', marginLeft: 4 }}>
//         <InfoCircle size={12} className="text-muted" />
//       </span>
//     </OverlayTrigger>
//   );

//   /* ─── product row ─── */
//   const renderProductRow = (product, idx, isSold, showOutlet = false) => {
//     const meta = foodTypeMeta[product?.foodType] || { bg: '#6c757d', label: product?.foodType };
//     return (
//       <tr key={`${product._id}-${idx}`} style={{ backgroundColor: isSold ? 'white' : '#fafafa', opacity: isSold ? 1 : 0.7 }}>
//         <td style={{ ...tdStyle, color: '#94a3b8', fontSize: 12 }}>{idx + 1}</td>
//         <td style={tdStyle}>
//           <div style={{ fontWeight: 500, color: '#1e293b', fontSize: 13 }}>{product?.productName || '----'}</div>
//           <div style={{ fontSize: 11, marginTop: 2 }}>
//             <span style={{ color: '#0d6efd', fontWeight: 600 }}>{product?.categoryName || '----'}</span>
//             {showOutlet && product?._outletNames?.length > 0 && (
//               <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 10 }}>
//                 · {product?._outletNames?.join(', ')}
//               </span>
//             )}
//             {showOutlet && !product?._outletNames && product?._outletName && (
//               <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 10 }}>· {product._outletName}</span>
//             )}
//           </div>
//         </td>
//         <td style={tdStyle}>
//           <span style={{
//             display: 'inline-block', padding: '2px 8px', borderRadius: 20,
//             fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
//             backgroundColor: meta.bg + '18', color: meta.bg, border: `1px solid ${meta.bg}40`,
//           }}>{meta.label}</span>
//         </td>
//         <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13 }}>
//           {product?.rate ? `₹ ${Number(product.rate).toFixed(2)}` : <span style={{ color: '#cbd5e1' }}>0.00</span>}
//         </td>
//         <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13 }}>
//           {isSold && product?.averageRateOfProduct
//             ? `₹ ${Number(product.averageRateOfProduct).toFixed(2)}`
//             : <span style={{ color: '#cbd5e1' }}>0.00</span>}
//         </td>
//         <td style={{ ...tdStyle, textAlign: 'center' }}>
//           {isSold
//             ? <span style={{ fontWeight: 700, color: '#0d6efd', fontSize: 14 }}>{product?.totalQuantity ?? 0}</span>
//             : <span style={{ color: '#94a3b8', fontSize: 12 }}>0</span>}
//         </td>
//         <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13 }}>
//           {isSold ? fmt(product?.addonOfferPrice) : <span style={{ color: '#cbd5e1' }}>0.00</span>}
//         </td>
//         <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13 }}>
//           {isSold ? fmt(product?.actualTaxable) : <span style={{ color: '#cbd5e1' }}>0.00</span>}
//         </td>
//         <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13, color: isSold ? '#dc3545' : '#cbd5e1' }}>
//           {isSold ? fmt((product?.actualTaxable || 0) - (product?.totalTaxable || 0)) : '0.00'}
//         </td>
//         <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13, color: isSold ? '#16a34a' : '#cbd5e1', fontWeight: isSold ? 600 : 400 }}>
//           {isSold ? fmt(product?.totalTaxable) : '0.00'}
//         </td>
//       </tr>
//     );
//   };

//   /* ─── totals row ─── */
//   const TotalsRow = ({ label, totals, colSpan = 5 }) => (
//     <tr style={{ backgroundColor: '#1e3a5f' }}>
//       <td colSpan={colSpan} style={{ ...tdStyle, textAlign: 'right', color: 'white', backgroundColor: '#1e3a5f', fontWeight: 700, fontSize: 11, letterSpacing: '1px', textTransform: 'uppercase' }}>{label}</td>
//       <td style={{ ...tdStyle, textAlign: 'center', color: 'white', fontWeight: 700, backgroundColor: '#1e3a5f' }}>{totals.qty}</td>
//       <td style={{ ...tdStyle, textAlign: 'right', color: 'white', fontWeight: 600, backgroundColor: '#1e3a5f' }}>{fmt(totals.addon)}</td>
//       <td style={{ ...tdStyle, textAlign: 'right', color: 'white', fontWeight: 600, backgroundColor: '#1e3a5f' }}>{fmt(totals.actual)}</td>
//       <td style={{ ...tdStyle, textAlign: 'right', color: '#fca5a5', fontWeight: 600, backgroundColor: '#1e3a5f' }}>{fmt(totals.disc)}</td>
//       <td style={{ ...tdStyle, textAlign: 'right', color: '#86efac', fontWeight: 700, backgroundColor: '#1e3a5f' }}>{fmt(totals.afterDisc)}</td>
//     </tr>
//   );

//   /* ─── table head ─── */
//   const TableHead = ({ showOutletCol = false }) => (
//     <thead>
//       <tr>
//         <th style={{ ...thStyle, width: 44 }}>#</th>
//         <ThSortable colKey="productName">
//           {showOutletCol ? 'Product / Category / Outlet' : 'Category & Product'}
//         </ThSortable>
//         <ThSortable colKey="foodType">Type</ThSortable>
//         <ThSortable colKey="rate" align="right">Rate</ThSortable>
//         <ThSortable colKey="averageRateOfProduct" align="right">Avg. Price</ThSortable>
//         <ThSortable colKey="totalQuantity" align="center">Sales</ThSortable>
//         <ThSortable colKey="addonOfferPrice" align="right">Addon ₹</ThSortable>
//         <th style={{ ...thStyle, textAlign: 'right' }}>
//           Actual Taxable <InfoTip id="at" text="Average Price × Sales Count" />
//         </th>
//         <th style={{ ...thStyle, textAlign: 'right' }}>
//           Discount <InfoTip id="disc" text="Before Discount − After Discount Taxable" />
//         </th>
//         <th style={{ ...thStyle, textAlign: 'right' }}>
//           After Dis. Taxable <InfoTip id="adt" text="Taxable after all discounts applied" />
//         </th>
//       </tr>
//     </thead>
//   );

//   /* ─── outlet subtotals helper ─── */
//   const calcSubTotals = (products) => ({
//     qty:       safeArray(products)?.reduce((s, p) => s + (p?.totalQuantity || 0), 0),
//     addon:     safeArray(products)?.reduce((s, p) => s + (p?.addonOfferPrice || 0), 0),
//     actual:    safeArray(products)?.reduce((s, p) => s + (p?.actualTaxable || 0), 0),
//     disc:      safeArray(products)?.reduce((s, p) => s + ((p?.actualTaxable || 0) - (p?.totalTaxable || 0)), 0),
//     afterDisc: safeArray(products)?.reduce((s, p) => s + (p?.totalTaxable || 0), 0),
//   });

//   /* ─── category header row ─── */
//   const CategoryHeaderRow = ({ catName, products, collapseKey, isCollapsed, isSold = true }) => {
//     const totals = calcSubTotals(products);
//     return (
//       <tr
//         className="psr-special"
//         onClick={() => toggleCategoryCollapse(collapseKey)}
//         style={{
//           backgroundColor: isSold ? '#eef4ff' : '#f8f8f8',
//           cursor: 'pointer',
//           userSelect: 'none',
//         }}
//       >
//         <td colSpan={6} style={{ ...tdStyle, padding: '9px 12px' }}>
//           <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//             <span style={{ fontSize: 11, color: isSold ? '#0d6efd' : '#94a3b8', transition: 'transform 0.15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
//             <i className={`bi bi-tag${isSold ? '' : ''}`} style={{ fontSize: 12, color: isSold ? '#0d6efd' : '#94a3b8' }}></i>
//             <span style={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.4px', color: isSold ? '#0d6efd' : '#94a3b8', textTransform: 'uppercase' }}>
//               {catName}
//             </span>
//             <span style={{
//               display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
//               minWidth: 20, height: 18, padding: '0 6px',
//               borderRadius: 20, fontSize: 9, fontWeight: 700,
//               backgroundColor: isSold ? '#0d6efd' : '#94a3b8', color: 'white',
//             }}>
//               {products.length}
//             </span>
//           </div>
//         </td>
//         {isSold ? (
//           <>
//             <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b', fontSize: 11, fontWeight: 600 }}>{fmt(totals.addon)}</td>
//             <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b', fontSize: 11, fontWeight: 600 }}>{fmt(totals.actual)}</td>
//             <td style={{ ...tdStyle, textAlign: 'right', color: '#dc3545', fontSize: 11, fontWeight: 600 }}>{fmt(totals.disc)}</td>
//             <td style={{ ...tdStyle, textAlign: 'right', color: '#16a34a', fontSize: 12, fontWeight: 700 }}>{fmt(totals.afterDisc)}</td>
//           </>
//         ) : (
//           <td colSpan={4} style={{ ...tdStyle, color: '#94a3b8', fontSize: 11 }}>No sales in this period</td>
//         )}
//       </tr>
//     );
//   };

//   /* ════════════════════════════════ JSX ════════════════════════════════ */
//   return (
//     <React.Fragment>
//       <Header onSkin={setSkin} />

//       <style>{`
//         .psr-filter-box { background:#f8fafc; border:1px solid #e9ecef; border-radius:12px; padding:18px; }
//         .psr-table tbody tr:not(.psr-special):hover { background-color:#f0f7ff !important; transition:background 0.1s; }
//         .psr-stat-chip { display:inline-flex;align-items:center;gap:6px;background:#e7f0ff;border-radius:8px;padding:4px 12px;font-size:12px;color:#0d6efd;font-weight:600; }
//         .view-toggle-btn { border:1.5px solid #e2e8f0; border-radius:8px; padding:6px 16px; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.15s; background:white; color:#64748b; }
//         .view-toggle-btn.active { background:#0d6efd; color:white; border-color:#0d6efd; box-shadow:0 2px 8px rgba(13,110,253,.25); }
//         .outlet-card-header { background:linear-gradient(90deg,#eef4ff,#f8fafc); padding:10px 16px; border-bottom:1px solid #dbeafe; display:flex; align-items:center; gap:12px; }
//         .unsold-section-header { background:#f8f8f8; padding:5px 14px; border-top:1px solid #f1f5f9; font-size:10px; font-weight:700; letter-spacing:.8px; text-transform:uppercase; color:#94a3b8; }
//         .outlet-subtotal { background:#f0f7ff !important; }
//         .outlet-subtotal td { font-weight:700; }

//         /* ── Category rows ── */
//         .psr-cat-row-sold:hover td { background:#dbeafe !important; }
//         .psr-cat-row-unsold:hover td { background:#f1f5f9 !important; }

//         /* ── Outlet tabs ── */
//         .outlet-tabs-bar {
//           display: flex;
//           gap: 0;
//           flex-wrap: wrap;
//           background: #f8fafc;
//           border: 1px solid #e2e8f0;
//           border-bottom: none;
//           border-radius: 12px 12px 0 0;
//           padding: 8px 8px 0 8px;
//           overflow-x: auto;
//         }
//         .outlet-tab-btn {
//           display: inline-flex;
//           align-items: center;
//           gap: 7px;
//           padding: 9px 16px 10px 16px;
//           border: 1px solid transparent;
//           border-bottom: none;
//           border-radius: 8px 8px 0 0;
//           background: transparent;
//           color: #64748b;
//           font-size: 13px;
//           font-weight: 500;
//           cursor: pointer;
//           transition: all 0.15s;
//           white-space: nowrap;
//           position: relative;
//           margin-bottom: -1px;
//         }
//         .outlet-tab-btn:hover:not(.active) {
//           background: #eef4ff;
//           color: #0d6efd;
//         }
//         .outlet-tab-btn.active {
//           background: white;
//           color: #0d6efd;
//           font-weight: 700;
//           border-color: #e2e8f0;
//           border-bottom-color: white;
//           box-shadow: 0 -2px 0 0 #0d6efd inset;
//         }
//         .outlet-tab-badge {
//           display: inline-flex;
//           align-items: center;
//           justify-content: center;
//           min-width: 22px;
//           height: 18px;
//           padding: 0 6px;
//           border-radius: 20px;
//           font-size: 10px;
//           font-weight: 700;
//           background: #e2e8f0;
//           color: #64748b;
//           transition: all 0.15s;
//         }
//         .outlet-tab-btn.active .outlet-tab-badge {
//           background: #0d6efd;
//           color: white;
//         }
//         .outlet-tab-panel {
//           border: 1px solid #e2e8f0;
//           border-radius: 0 0 12px 12px;
//           overflow: hidden;
//         }
//       `}</style>

//       <div className={`main main-app p-3 p-lg-4 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
//         <div className="card border-0" style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.06),0 4px 16px rgba(0,0,0,.04)', border: '1px solid #e2e8f0' }}>

//           {/* ── Page Header ── */}
//           <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center" style={{ borderRadius: '16px 16px 0 0' }}>
//             <div className="d-flex align-items-center gap-3">
//               <div style={{ width:42, height:42, borderRadius:10, background:'linear-gradient(135deg,#0d6efd,#6ea8fe)', display:'flex', alignItems:'center', justifyContent:'center' }}>
//                 <i className="bi bi-box-seam text-white fs-5"></i>
//               </div>
//               <div>
//                 <h5 className="mb-0 fw-bold" style={{ color:'#1e293b', letterSpacing:'-0.3px' }}>PRODUCT SALES REPORT</h5>
//                 <div style={{ fontSize:12, color:'#94a3b8' }}>Analyze product performance across outlets</div>
//               </div>
//             </div>
//             <Button variant="outline-secondary" size="sm" onClick={() => navigate('/reports/all-reports')}
//               className="d-flex align-items-center fw-semibold" style={{ borderRadius:4, height:36 }}>
//               <ArrowLeft className="me-1" size={14} /> Back to Reports
//             </Button>
//           </div>
//           <div style={{ height:1, background:'#e2e8f0' }} />

//           <div className="card-body p-4">
//             <Form onSubmit={handleSubmit}>
//               {/* ── Filters ── */}
//               <div className="psr-filter-box mb-3">
//                 <Row className="g-3">
//                   <Col md={3}>
//                     <OutletSelect value={selectedOutlets} onChange={(opts) => setSelectedOutlets(safeArray(opts))} isMulti={true} />
//                   </Col>
//                   <Col md={3}>
//                     <Form.Label className="fw-semibold" style={{ fontSize:14, color:'#475569' }}>
//                       <i className="bi bi-tags me-1"></i>Category
//                     </Form.Label>
//                     <Select options={categoryOptions} value={selectedCategories}
//                       onChange={(opts) => setSelectedCategories(safeArray(opts))}
//                       isMulti isClearable closeMenuOnSelect={false}
//                       styles={selectStyles} placeholder="All Categories" />
//                   </Col>
//                   <Col md={2}>
//                     <Form.Label className="fw-semibold" style={{ fontSize:14, color:'#475569' }}>
//                       <i className="bi bi-list-check me-1"></i>Class Type
//                     </Form.Label>
//                     <Select options={classTypeOptions} value={classTypeOptions.find(o => o.value === classType) || null}
//                       onChange={(opt) => setClassType(opt?.value || null)}
//                       isClearable styles={selectStyles} placeholder="All Types" />
//                   </Col>
//                   <Col md={2}>
//                     <Form.Label className="fw-semibold" style={{ fontSize:14, color:'#475569' }}>
//                       <i className="bi bi-calendar3 me-1"></i>Start Date
//                     </Form.Label>
//                     <DatePicker selected={startDate} onChange={(date) => {
//                       setStartDate(date);
//                       if (endDate && date && endDate > new Date(date.getTime() + MAX_DATE_RANGE_DAYS * 86400000)) setEndDate(null);
//                     }} dateFormat="dd-MM-yyyy" className="form-control" />
//                   </Col>
//                   <Col md={2}>
//                     <Form.Label className="fw-semibold" style={{ fontSize:14, color:'#475569' }}>
//                       <i className="bi bi-calendar3 me-1"></i>End Date
//                     </Form.Label>
//                     <DatePicker selected={endDate} onChange={(date) => {
//                       if (startDate && date > new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 86400000)) setError(DATE_RANGE_ERROR_MESSAGE);
//                       else { setError(''); handleEndDateChange(date); }
//                     }} dateFormat="dd-MM-yyyy" minDate={startDate}
//                       maxDate={startDate ? new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 86400000) : null}
//                       className="form-control" />
//                     {error && <div className="text-danger" style={{ fontSize:11, marginTop:4 }}>{error}</div>}
//                   </Col>
//                 </Row>
//               </div>

//               {/* ── Actions bar ── */}
//               <div className="psr-filter-box mb-4">
//                 <Row className="g-3 align-items-end">
//                   <Col md={3}>
//                     <Button variant="primary" size="sm" type="submit" className="w-100 fw-semibold"
//                       disabled={!startDate || !endDate} style={{ borderRadius:4, height:35 }}>
//                       {btnLoading
//                         ? <><Spinner size="sm" animation="border" className="me-2" />Loading…</>
//                         : <><i className="bi bi-bar-chart-line me-2"></i>Generate Report</>}
//                     </Button>
//                   </Col>
//                   <Col md={2}>
//                     <DropdownButton variant="outline-success" className="fw-semibold w-100"
//                       title={<>{downloadBtnLoading ? <Spinner size="sm" className="me-2" /> : <FileEarmarkSpreadsheet className="me-2" />}Download Report</>}
//                       disabled={!hasAnyData || downloadBtnLoading}>
//                       <Dropdown.Item onClick={() => handleDownloadExcel(false)} disabled={downloadBtnLoading}>
//                         <FileEarmarkSpreadsheet className="me-2 text-success" />Download without costing
//                       </Dropdown.Item>
//                       <Dropdown.Item onClick={() => handleDownloadExcel(true)} disabled={downloadBtnLoading}>
//                         <FileEarmarkSpreadsheet className="me-2 text-success" />Download with costing
//                       </Dropdown.Item>
//                     </DropdownButton>
//                   </Col>
//                   <Col md={4}>
//                     <InputGroup>
//                       <Form.Control type="text" placeholder="Search by product name…" value={searchText}
//                         onChange={(e) => { setSearchText(e.target.value); setIsTouched(true); }}
//                         style={{ borderRadius:'4px 0 0 4px', fontSize:13 }} />
//                       <Button variant="primary" style={{ zIndex:0, borderRadius:'0 4px 4px 0' }}
//                         onClick={() => setIsTouched(true)} disabled={!searchText.trim()}>
//                         <Search size={13} className="me-1" />Search
//                       </Button>
//                     </InputGroup>
//                   </Col>

//                   {/* stat chips + view toggle */}
//                   {hasAnyData && (
//                     <>
//                       <Col xs={12} md={8} className="d-flex gap-2 flex-wrap pt-1">
//                         <span className="psr-stat-chip"><i className="bi bi-shop"></i>{filteredReportData.length} Outlet{filteredReportData.length !== 1 ? 's' : ''}</span>
//                         <span className="psr-stat-chip"><i className="bi bi-bag-check"></i>{grandTotals.qty} Units Sold</span>
//                         <span className="psr-stat-chip" style={{ background:'#dcfce7', color:'#16a34a' }}>
//                           <i className="bi bi-currency-rupee"></i>{fmt(grandTotals.afterDisc)} Revenue
//                         </span>
//                         <span className="psr-stat-chip" style={{ background:'#fee2e2', color:'#dc3545' }}>
//                           <i className="bi bi-tag"></i>{fmt(grandTotals.disc)} Discounts
//                         </span>
//                       </Col>

//                       <Col md="auto" className="ms-auto">
//                         <div className="d-flex gap-2">
//                           <button type="button"
//                             className={`view-toggle-btn ${viewMode === VIEW_COMBINED ? 'active' : ''}`}
//                             onClick={() => setViewMode(VIEW_COMBINED)}>
//                             <i className="bi bi-table me-2"></i>Combined
//                           </button>
//                           <button type="button"
//                             className={`view-toggle-btn ${viewMode === VIEW_CATEGORY ? 'active' : ''}`}
//                             onClick={() => setViewMode(VIEW_CATEGORY)}>
//                             <i className="bi bi-grid-3x3-gap me-2"></i>Category Wise
//                           </button>
//                           <button type="button"
//                             className={`view-toggle-btn ${viewMode === VIEW_GROUPED ? 'active' : ''}`}
//                             onClick={() => setViewMode(VIEW_GROUPED)}>
//                             <i className="bi bi-layout-split me-2"></i>Outlet Wise
//                           </button>
//                         </div>
//                       </Col>
//                     </>
//                   )}
//                 </Row>
//               </div>
//             </Form>

//             {/* ── Empty state ── */}
//             {!clicked ? (
//               <div className="d-flex flex-column justify-content-center align-items-center py-5 my-3 rounded-3"
//                 style={{ background:'#f8fafc', border:'2px dashed #e2e8f0', minHeight:240 }}>
//                 <div style={{ width:64, height:64, borderRadius:16, background:'#e7f0ff', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
//                   <i className="bi bi-clipboard-data text-primary fs-3"></i>
//                 </div>
//                 <h6 className="text-muted mb-1">No data to display</h6>
//                 <p className="text-muted mb-0" style={{ fontSize:13 }}>Select filters above and click <strong>Generate Report</strong></p>
//               </div>

//             ) : loading ? (
//               <div className="text-center py-5">
//                 <Spinner animation="border" variant="primary" />
//                 <p className="mt-2 mb-0 text-muted" style={{ fontSize:13 }}>Loading product data…</p>
//               </div>

//             ) : !hasAnyData ? (
//               <div className="text-center py-5" style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:12 }}>
//                 <i className="bi bi-inbox fs-3 text-muted d-block mb-2"></i>
//                 <span className="text-muted" style={{ fontSize:13 }}>No products found for the selected criteria</span>
//               </div>

//             ) : viewMode === VIEW_COMBINED ? (
//               /* ══════════ COMBINED VIEW ══════════ */
//               <div style={{ borderRadius:12, overflow:'hidden', border:'1px solid #e2e8f0' }}>
//                 <Table className="mb-0 psr-table" style={{ fontSize:13 }}>
//                   <TableHead showOutletCol={filteredReportData.length > 1} />
//                   <tbody>
//                     {sortProducts(combinedProducts).map((p, i) =>
//                       renderProductRow(p, i, true, filteredReportData.length > 1)
//                     )}
//                     <TotalsRow label="Grand Total" totals={grandTotals} />

//                     {combinedUnsold.length > 0 && (
//                       <tr className="psr-special">
//                         <td colSpan="10" style={{ padding:'6px 16px', background:'#f8f8f8', fontSize:10, fontWeight:700, letterSpacing:'.8px', textTransform:'uppercase', color:'#94a3b8', borderTop:'2px solid #e2e8f0' }}>
//                           <i className="bi bi-slash-circle me-2"></i>Unsold Products ({combinedUnsold.length})
//                         </td>
//                       </tr>
//                     )}
//                     {sortProducts(combinedUnsold).map((p, i) =>
//                       renderProductRow(p, i, false, filteredReportData.length > 1)
//                     )}
//                   </tbody>
//                 </Table>
//               </div>

//             ) : viewMode === VIEW_CATEGORY ? (
//               /* ══════════ CATEGORY WISE VIEW ══════════ */
//               <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
//                 {/* Expand / Collapse All controls */}
//                 <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
//                   <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
//                     {groupedByCategory.length} categor{groupedByCategory.length !== 1 ? 'ies' : 'y'}
//                   </span>
//                   <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
//                     <button
//                       type="button"
//                       onClick={() => {
//                         const keys = {};
//                         groupedByCategory.forEach(([k]) => { keys[k] = false; });
//                         groupedUnsoldByCategory.forEach(([k]) => { keys[`unsold_${k}`] = false; });
//                         setCategoryCollapsed(keys);
//                       }}
//                       style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', color: '#0d6efd', cursor: 'pointer', fontWeight: 600 }}>
//                       <i className="bi bi-arrows-expand me-1"></i>Expand All
//                     </button>
//                     <button
//                       type="button"
//                       onClick={() => {
//                         const keys = {};
//                         groupedByCategory.forEach(([k]) => { keys[k] = true; });
//                         groupedUnsoldByCategory.forEach(([k]) => { keys[`unsold_${k}`] = true; });
//                         setCategoryCollapsed(keys);
//                       }}
//                       style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>
//                       <i className="bi bi-arrows-collapse me-1"></i>Collapse All
//                     </button>
//                   </div>
//                 </div>

//                 <Table className="mb-0 psr-table" style={{ fontSize: 13 }}>
//                   <TableHead showOutletCol={filteredReportData.length > 1} />
//                   <tbody>
//                     {/* ── Sold products grouped by category ── */}
//                     {groupedByCategory.map(([catName, products]) => {
//                       const isCollapsed = categoryCollapsed[catName];
//                       return (
//                         <React.Fragment key={catName}>
//                           <CategoryHeaderRow
//                             catName={catName}
//                             products={products}
//                             collapseKey={catName}
//                             isCollapsed={isCollapsed}
//                             isSold={true}
//                           />
//                           {!isCollapsed && sortProducts(products).map((p, i) =>
//                             renderProductRow(p, i, true, filteredReportData.length > 1)
//                           )}
//                         </React.Fragment>
//                       );
//                     })}

//                     {/* ── Grand total ── */}
//                     <TotalsRow label="Grand Total" totals={grandTotals} />

//                     {/* ── Unsold products section ── */}
//                     {groupedUnsoldByCategory.length > 0 && (
//                       <tr className="psr-special">
//                         <td colSpan="10" style={{
//                           padding: '6px 16px', background: '#f8f8f8',
//                           fontSize: 10, fontWeight: 700, letterSpacing: '.8px',
//                           textTransform: 'uppercase', color: '#94a3b8',
//                           borderTop: '2px solid #e2e8f0',
//                         }}>
//                           <i className="bi bi-slash-circle me-2"></i>
//                           Unsold Products — by Category ({groupedUnsoldByCategory.reduce((s, [, p]) => s + p.length, 0)})
//                         </td>
//                       </tr>
//                     )}
//                     {groupedUnsoldByCategory.map(([catName, products]) => {
//                       const collapseKey = `unsold_${catName}`;
//                       const isCollapsed = categoryCollapsed[collapseKey];
//                       return (
//                         <React.Fragment key={collapseKey}>
//                           <CategoryHeaderRow
//                             catName={catName}
//                             products={products}
//                             collapseKey={collapseKey}
//                             isCollapsed={isCollapsed}
//                             isSold={false}
//                           />
//                           {!isCollapsed && sortProducts(products).map((p, i) =>
//                             renderProductRow(p, i, false, filteredReportData.length > 1)
//                           )}
//                         </React.Fragment>
//                       );
//                     })}

                    
//                   </tbody>
//                 </Table>
//               </div>

//             ) : (
//               /* ══════════ GROUPED BY OUTLET VIEW — TABBED ══════════ */
//               <div>
//                 {/* ── Tab bar ── */}
//                 <div className="outlet-tabs-bar">
//                   {filteredReportData.map((outletData, idx) => {
//                     const unitsSold = safeArray(outletData?.products)?.reduce((s, p) => s + (p?.totalQuantity || 0), 0);
//                     const isActive  = activeOutletTab === idx;
//                     return (
//                       <button
//                         key={outletData?.outlet?._id}
//                         type="button"
//                         className={`outlet-tab-btn ${isActive ? 'active' : ''}`}
//                         onClick={() => setActiveOutletTab(idx)}
//                       >
//                         <i className="bi bi-shop" style={{ fontSize: 11 }}></i>
//                         {outletData?.outlet?.name}
//                         <span className="outlet-tab-badge">{unitsSold}</span>
//                       </button>
//                     );
//                   })}
//                 </div>

//                 {/* ── Active outlet panel ── */}
//                 {(() => {
//                   const outletData = filteredReportData[activeOutletTab];
//                   if (!outletData) return null;
//                   const { outlet, products, unSoldProducts } = outletData;
//                   const sold   = sortProducts(products);
//                   const unsold = sortProducts(unSoldProducts);
//                   const sub    = calcSubTotals(products);

//                   return (
//                     <div className="outlet-tab-panel">
//                       {/* KPI strip */}
//                       <div className="outlet-card-header">
//                         <div style={{ width:32, height:32, borderRadius:8, background:'#0d6efd22', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
//                           <i className="bi bi-shop" style={{ color:'#0d6efd', fontSize:15 }}></i>
//                         </div>
//                         <div style={{ flex:1 }}>
//                           <div style={{ fontWeight:700, color:'#0d6efd', fontSize:14 }}>{outlet?.name}</div>
//                           <div style={{ fontSize:11, color:'#94a3b8' }}>{outlet?.address}</div>
//                         </div>
//                         <div className="d-flex gap-3 flex-wrap">
//                           <div style={{ textAlign:'center' }}>
//                             <div style={{ fontSize:18, fontWeight:800, color:'#0d6efd', lineHeight:1 }}>{sub.qty}</div>
//                             <div style={{ fontSize:10, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px' }}>Units</div>
//                           </div>
//                           <div style={{ width:1, background:'#dbeafe' }} />
//                           <div style={{ textAlign:'center' }}>
//                             <div style={{ fontSize:14, fontWeight:800, color:'#16a34a', lineHeight:1 }}>{fmt(sub.afterDisc)}</div>
//                             <div style={{ fontSize:10, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px' }}>Revenue</div>
//                           </div>
//                           <div style={{ width:1, background:'#dbeafe' }} />
//                           <div style={{ textAlign:'center' }}>
//                             <div style={{ fontSize:14, fontWeight:700, color:'#dc3545', lineHeight:1 }}>{fmt(sub.disc)}</div>
//                             <div style={{ fontSize:10, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px' }}>Discount</div>
//                           </div>
//                           <div style={{ width:1, background:'#dbeafe' }} />
//                           <div className="d-flex gap-2 align-items-center">
//                             <span style={{ fontSize:11, background:'#dbeafe', color:'#1d4ed8', borderRadius:6, padding:'3px 10px', fontWeight:600 }}>
//                               {safeArray(products).length} sold
//                             </span>
//                             <span style={{ fontSize:11, background:'#f1f5f9', color:'#64748b', borderRadius:6, padding:'3px 10px', fontWeight:600 }}>
//                               {safeArray(unSoldProducts).length} unsold
//                             </span>
//                           </div>
//                         </div>
//                       </div>

//                       {/* Product table */}
//                       <Table className="mb-0 psr-table" style={{ fontSize:13 }}>
//                         <TableHead />
//                         <tbody>
//                           {sold.length > 0
//                             ? sold.map((p, i) => renderProductRow(p, i, true))
//                             : (
//                               <tr className="psr-special">
//                                 <td colSpan="10" className="text-center text-muted py-3 fst-italic" style={{ fontSize:12, background:'#fafafa' }}>
//                                   No sold products in this period
//                                 </td>
//                               </tr>
//                             )}

//                           {unsold.length > 0 && (
//                             <>
//                               <tr className="psr-special">
//                                 <td colSpan="10" className="unsold-section-header">
//                                   <i className="bi bi-slash-circle me-2"></i>Unsold Products ({unsold.length})
//                                 </td>
//                               </tr>
//                               {unsold.map((p, i) => renderProductRow(p, i, false))}
//                             </>
//                           )}

//                           <tr className="outlet-subtotal psr-special">
//                             <td colSpan="5" style={{ ...tdStyle, textAlign:'right', fontSize:11, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'.5px' }}>
//                               Subtotal
//                             </td>
//                             <td style={{ ...tdStyle, textAlign:'center', color:'#0d6efd' }}>{sub.qty}</td>
//                             <td style={{ ...tdStyle, textAlign:'right' }}>{fmt(sub.addon)}</td>
//                             <td style={{ ...tdStyle, textAlign:'right' }}>{fmt(sub.actual)}</td>
//                             <td style={{ ...tdStyle, textAlign:'right', color:'#dc3545' }}>{fmt(sub.disc)}</td>
//                             <td style={{ ...tdStyle, textAlign:'right', color:'#16a34a' }}>{fmt(sub.afterDisc)}</td>
//                           </tr>
//                         </tbody>
//                       </Table>
//                     </div>
//                   );
//                 })()}

//                 {filteredReportData.length > 1 && (
//                   <div style={{ marginTop:12, borderRadius:10, overflow:'hidden', border:'1px solid #1e3a5f' }}>
//                     <Table className="mb-0" style={{ fontSize:13 }}>
//                       <tbody>
//                         <TotalsRow label="Grand Total — All Outlets" totals={grandTotals} />
//                       </tbody>
//                     </Table>
//                   </div>
//                 )}
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </React.Fragment>
//   );
// };

// export default ProductSalesReport;